import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import {
  BAUD_OPTIONS,
  CMD_COUNT_OPTIONS,
  MODEL_OPTIONS,
  buildCommandPacket,
  defaultCommandSlot,
} from "../commandUtils.js";
import {
  CommandSlotEditor,
  FieldLabel,
  SectionCard,
  SectionHeader,
  Segmented,
  buildCommandTheme,
} from "./commandBuilderControls.jsx";
import { CommandPreviewPanel } from "./CommandPreviewPanel.jsx";

export function CreateCanCommandModal({ open, onClose, onSave, editing = null, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const theme = useMemo(() => buildCommandTheme(dark, c, A), [dark, c, A]);
  const { input, focus } = theme;

  const [modelId, setModelId] = useState(MODEL_OPTIONS[0].id);
  const [commandName, setCommandName] = useState("");
  const [description, setDescription] = useState("");
  const [baudrate, setBaudrate] = useState(500);
  const [cmdCount, setCmdCount] = useState(1);
  const [slots, setSlots] = useState([defaultCommandSlot(0)]);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isEdit = Boolean(editing);

  useLayoutEffect(() => {
    if (!open) return;
    setPreviewExpanded(false);
    setSaveNotice(null);
    setCopied(false);
    setSubmitError(null);
    if (editing) {
      setCommandName(editing.name ?? "");
      setDescription(editing.description ?? "");
      setBaudrate(editing.baud ?? 500);
      const match = MODEL_OPTIONS.find((m) => m.label === editing.model);
      setModelId(match?.id ?? MODEL_OPTIONS[0].id);
      setCmdCount(1);
      setSlots([defaultCommandSlot(0)]);
    } else {
      setModelId(MODEL_OPTIONS[0].id);
      setCommandName("");
      setDescription("");
      setBaudrate(500);
      setCmdCount(1);
      setSlots([defaultCommandSlot(0)]);
    }
  }, [open, editing]);

  const syncSlotCount = useCallback((n) => {
    setCmdCount(n);
    setSlots((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(defaultCommandSlot(next.length));
      return next.slice(0, n);
    });
  }, []);

  const packet = useMemo(
    () => buildCommandPacket({ baudrate, cmdCount, slots }),
    [slots, baudrate, cmdCount],
  );

  const modelLabel = MODEL_OPTIONS.find((m) => m.id === modelId)?.label ?? "";

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(packet.rawHex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  const handleSave = async () => {
    setSubmitError(null);
    const payload = {
      name: commandName.trim() || "untitled",
      description: description.trim(),
      commandType: "can",
      model: modelLabel,
      commandHex: packet.rawHex.replace(/\s/g, ""),
      baud: baudrate,
    };
    try {
      await onSave(payload);
      setSaveNotice("Saved");
      window.setTimeout(() => setSaveNotice(null), 2200);
      onClose();
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save command");
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="can-command-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(960px, calc(100vw - 24px))",
          height: "calc(100vh - 48px)",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 16,
          zIndex: 71,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit CAN command" : "New CAN command"}
          titleId="can-command-modal-title"
          subtitle="Configure CAN bus frames and preview the command packet."
          icon={Ico.chip}
          onClose={onClose}
          closeLabel="Close CAN command modal"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "16px 20px 20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <SectionCard theme={theme}>
              <SectionHeader title="General" hint="Model, name, description, and bus parameters." icon={Ico.chip} A={A} c={c} />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
                  gap: 16,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <FieldLabel c={c}>Model</FieldLabel>
                  <select value={modelId} onChange={(e) => setModelId(e.target.value)} style={{ ...input, cursor: "pointer" }} {...focus}>
                    {MODEL_OPTIONS.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ minWidth: 0 }}>
                  <FieldLabel c={c}>Command name</FieldLabel>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: c.sub, pointerEvents: "none", display: "flex" }}>
                      {Ico.terminal}
                    </span>
                    <input
                      type="text"
                      value={commandName}
                      onChange={(e) => setCommandName(e.target.value)}
                      placeholder="e.g. BMS Wake"
                      style={{ ...input, paddingLeft: 34 }}
                      {...focus}
                    />
                  </div>
                </div>
                <div style={{ minWidth: 0, gridColumn: "1 / -1" }}>
                  <FieldLabel c={c}>Description</FieldLabel>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What this command does, when to use it, etc."
                    rows={3}
                    style={{
                      ...input,
                      height: "auto",
                      minHeight: 72,
                      padding: "10px 11px",
                      resize: "vertical",
                      lineHeight: 1.45,
                    }}
                    {...focus}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <FieldLabel c={c}>Baudrate (kbps)</FieldLabel>
                  <Segmented options={BAUD_OPTIONS} value={baudrate} onChange={setBaudrate} dark={dark} c={c} A={A} fluid />
                </div>
                <div style={{ minWidth: 0 }}>
                  <FieldLabel c={c}>Frames in sequence</FieldLabel>
                  <Segmented options={CMD_COUNT_OPTIONS} value={cmdCount} onChange={syncSlotCount} dark={dark} c={c} A={A} fluid />
                </div>
              </div>
            </SectionCard>

            <SectionCard theme={theme}>
              <SectionHeader
                title="Frame builder"
                hint="Define each CAN frame — ARB ID, DLC, payload, and timing."
                icon={Ico.terminal}
                A={A}
                c={c}
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: slots.length > 1 ? "repeat(auto-fit, minmax(min(100%, 300px), 1fr))" : "1fr",
                  gap: 12,
                }}
              >
                {slots.map((slot, i) => (
                  <CommandSlotEditor
                    key={i}
                    index={i}
                    slot={slot}
                    onChange={(next) => setSlots((prev) => prev.map((s, j) => (j === i ? next : s)))}
                    dark={dark}
                    c={c}
                    A={A}
                    theme={theme}
                  />
                ))}
              </div>
            </SectionCard>

            {submitError && (
              <p style={{ margin: 0, fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
            )}

            <CommandPreviewPanel
              packet={packet}
              previewExpanded={previewExpanded}
              setPreviewExpanded={setPreviewExpanded}
              copied={copied}
              copyPreview={copyPreview}
              saveNotice={saveNotice}
              onSave={handleSave}
              saving={saving}
              dark={dark}
              c={c}
              A={A}
              theme={theme}
            />
          </div>
        </div>
      </div>
    </>
  );
}
